import React from 'react';
import '../Signup.css';

interface BasicSignupSkinProps {
  data: any;
  actions: any;
  options: any;
  mode: 'editor' | 'preview' | 'production';
  utils: any;
  app?: any;
  editor?: any;
}

const BasicSignupSkin: React.FC<BasicSignupSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils,
  app,
  editor 
}) => {
  const { t } = utils;
  
  // Extract data with safe defaults
  const {
    formData = {},
    validationErrors = {},
    loading = false,
    signUpSuccess = false,
    // signUpError, // TODO: Add error display
    isSpecifiedLegPolicy = false,
    companyId,
    theme = {},
    basicFields = {
      userId: true,
      password: true,
      name: true,
      phone: true,
      email: true,
      birthday: true,
      address: true,
      referralCode: true
    },
    varFields = {}
  } = data || {};
  
  // Extract actions with safe defaults
  const {
    handleChange = () => {},
    handleSubmit = () => {},
    handleBlur = () => {},
    handleRadioChange = () => {},
    handleCheckboxChange = () => {}
  } = actions || {};
  
  // Extract options with defaults
  const {
    title = t('회원가입'),
    showTitle = true,
    titleColor = theme?.primaryColor || '#333333',
    titleSize = '24px',
    formBackgroundColor = '#ffffff',
    formBorderRadius = '8px',
    formPadding = '20px',
    inputBorderColor = '#ddd',
    // inputFocusBorderColor = theme?.primaryColor || '#007bff', // TODO: Implement focus styles
    buttonColor = theme?.primaryColor || '#007bff',
    buttonTextColor = '#ffffff',
    buttonHoverColor = theme?.primaryColor || '#0056b3',
    errorColor = '#dc3545',
    successBackgroundColor = '#d4edda',
    successTextColor = '#155724',
    sectionTitleColor = '#333333',
    labelColor = '#333333',
    requiredMarkColor = '#dc3545'
  } = options;
  
  // Style objects
  const containerStyle = {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto'
  };
  
  const titleStyle = {
    fontSize: titleSize,
    color: titleColor,
    marginBottom: '20px',
    textAlign: 'center' as const
  };
  
  const formStyle = {
    backgroundColor: formBackgroundColor,
    borderRadius: formBorderRadius,
    padding: formPadding,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  };
  
  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: 'bold',
    color: sectionTitleColor,
    marginBottom: '15px',
    marginTop: '25px'
  };
  
  const labelStyle = {
    color: labelColor,
    marginBottom: '5px',
    display: 'block'
  };
  
  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: `1px solid ${inputBorderColor}`,
    borderRadius: '4px',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const
  };
  
  const inputErrorStyle = {
    ...inputStyle,
    borderColor: errorColor
  };
  
  const errorMessageStyle = {
    color: errorColor,
    fontSize: '12px',
    marginTop: '5px'
  };
  
  const buttonStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: buttonColor,
    color: buttonTextColor,
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    transition: 'background-color 0.2s',
    marginTop: '20px'
  };
  
  const successStyle = {
    backgroundColor: successBackgroundColor,
    color: successTextColor,
    padding: '20px',
    borderRadius: '4px',
    textAlign: 'center' as const
  };
  
  // Render var field
  const renderVarField = (fieldName: string, fieldConfig: any, styleOverride: React.CSSProperties = {}) => {
    if (!fieldConfig || !fieldConfig.show) {
      return null;
    }

    const fieldValue = formData[fieldName as keyof typeof formData]?.toString() || '';
    const [bankCode = '', accountNumber = ''] = fieldValue.includes('|') ? fieldValue.split('|') : ['', ''];
    const [ssnFront = '', ssnBack = ''] = fieldValue.includes('-') ? fieldValue.split('-') : ['', ''];

    switch (fieldConfig.type) {
      case 'select':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label htmlFor={fieldName} style={labelStyle}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* </span>}
              {fieldConfig.label}
            </label>
            <select
              id={fieldName}
              style={validationErrors[fieldName] ? inputErrorStyle : inputStyle}
              value={formData[fieldName as keyof typeof formData] as string}
              onChange={handleChange}
              required={fieldConfig.required}
            >
              <option value="">{t('선택하세요')}</option>
              {fieldConfig.options?.map((option: any, index: number) => {
                const optionValue = option.value || option.label;
                return (
                  <option key={index} value={optionValue}>
                    {option.label}
                  </option>
                );
              })}
            </select>
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'radio':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label style={{ ...labelStyle, marginBottom: '10px' }}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* </span>}
              {fieldConfig.label}
            </label>
            <div className="signup-radio-group">
              {fieldConfig.options?.map((option: any, index: number) => (
                <div className="signup-radio-item" key={index}>
                  <input 
                    type="radio" 
                    id={`${fieldName}_${index}`}
                    name={fieldName}
                    value={option.value}
                    checked={formData[fieldName as keyof typeof formData] === option.value}
                    onChange={() => handleRadioChange(fieldName, option.value)}
                    required={fieldConfig.required}
                    className="signup-radio"
                  />
                  <label htmlFor={`${fieldName}_${index}`} className="signup-radio-label">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'document':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label style={{ ...labelStyle, marginBottom: '10px' }}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* 필수 : </span>}
              {fieldConfig.label}
            </label>
            <div className="signup-checkbox-container">
              <input 
                type="checkbox" 
                id={fieldName} 
                checked={formData[fieldName as keyof typeof formData] ? true : false} 
                onChange={(e) => handleCheckboxChange(fieldName, fieldConfig.label, e.target.checked)}
                className="signup-checkbox"
                required={fieldConfig.required}
              />
              <label htmlFor={fieldName} className="signup-checkbox-label">
                동의합니다
              </label>
            </div>
            <div style={{ position: 'relative' }}>
              <textarea 
                className="signup-textarea"
                value={fieldConfig.content || ''}
                readOnly
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '10px',
                  border: `1px solid ${inputBorderColor}`,
                  borderRadius: '4px',
                  resize: 'none',
                  backgroundColor: '#f8f9fa'
                }}
              />
            </div>
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'account':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label style={labelStyle}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* </span>}
              {fieldConfig.label}
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select
                style={{ ...inputStyle, flex: '0 0 150px' }}
                value={bankCode}
                onChange={(e) => {
                  const newValue = `${e.target.value}|${accountNumber || ''}`;
                  handleChange({
                    target: { id: fieldName, value: newValue }
                  } as any);
                }}
                required={fieldConfig.required}
              >
                <option value="">{t('은행 선택')}</option>
                <option value="KB">{t('KB국민은행')}</option>
                <option value="SH">{t('신한은행')}</option>
                <option value="WR">{t('우리은행')}</option>
                <option value="HN">{t('하나은행')}</option>
                <option value="NH">{t('NH농협은행')}</option>
                <option value="KP">{t('우체국')}</option>
                <option value="IBK">{t('IBK기업은행')}</option>
                <option value="KDB">{t('KDB산업은행')}</option>
                <option value="SC">{t('SC제일은행')}</option>
                <option value="CT">{t('씨티은행')}</option>
                <option value="DG">{t('대구은행')}</option>
                <option value="BS">{t('부산은행')}</option>
                <option value="GJ">{t('광주은행')}</option>
                <option value="JJ">{t('제주은행')}</option>
                <option value="JB">{t('전북은행')}</option>
                <option value="KN">{t('경남은행')}</option>
                <option value="KK">{t('카카오뱅크')}</option>
                <option value="TS">{t('토스뱅크')}</option>
              </select>
              <input
                type="text"
                style={{ ...inputStyle, flex: '1' }}
                placeholder={t('계좌번호 입력')}
                value={accountNumber}
                onChange={(e) => {
                  const newValue = `${bankCode || ''}|${e.target.value}`;
                  handleChange({
                    target: { id: fieldName, value: newValue }
                  } as any);
                }}
                required={fieldConfig.required}
              />
            </div>
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'ssn':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label style={labelStyle}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* </span>}
              {fieldConfig.label}
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                style={{ ...inputStyle, flex: '1' }}
                placeholder={t('앞자리 (6자리)')}
                maxLength={6}
                value={ssnFront}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const newValue = `${value}-${ssnBack || ''}`;
                  handleChange({
                    target: { id: fieldName, value: newValue }
                  } as any);
                }}
                required={fieldConfig.required}
              />
              <span style={{ fontSize: '20px', color: '#666' }}>-</span>
              <input
                type="password"
                style={{ ...inputStyle, flex: '1' }}
                placeholder={t('뒷자리 (7자리)')}
                maxLength={7}
                value={ssnBack}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const newValue = `${ssnFront || ''}-${value}`;
                  handleChange({
                    target: { id: fieldName, value: newValue }
                  } as any);
                }}
                required={fieldConfig.required}
              />
            </div>
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      default: // input
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label htmlFor={fieldName} style={labelStyle}>
              {fieldConfig.required && <span style={{ color: requiredMarkColor }}>* </span>}
              {fieldConfig.label}
            </label>
            <input 
              id={fieldName}
              type="text" 
              placeholder={`${fieldConfig.label} ${t('입력')}`}
              style={validationErrors[fieldName] ? inputErrorStyle : inputStyle}
              value={formData[fieldName as keyof typeof formData] as string}
              onChange={handleChange}
              onBlur={() => handleBlur(fieldName)}
              required={fieldConfig.required}
            />
            {validationErrors[fieldName] && (
              <div style={errorMessageStyle}>
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
    }
  };
  
  return (
    <div className="signup-form" style={containerStyle}>
      {showTitle && <h2 style={titleStyle}>{title}</h2>}
      
      {signUpSuccess ? (
        <div style={successStyle}>
          <p>{t('회원가입이 완료되었습니다!')}</p>
          <p>{t('잠시 후 홈페이지로 이동합니다...')}</p>
        </div>
      ) : (
        <form style={formStyle} onSubmit={handleSubmit}>
          <div style={sectionTitleStyle}>{t('회원 정보')}</div>
          
          {/* Basic fields */}
          <div className="signup-form-row">
            {basicFields.userId && (
              <div className="signup-form-group">
                <label htmlFor="user_id" style={labelStyle}>
                  <span style={{ color: requiredMarkColor }}>* </span>
                  {t('아이디')}
                </label>
                <input 
                  id="user_id" 
                  type="text" 
                  placeholder={t('아이디 입력 (2~20자)')} 
                  required 
                  style={validationErrors.user_id ? inputErrorStyle : inputStyle}
                  value={formData.user_id}
                  onChange={handleChange}
                  onBlur={() => handleBlur('user_id')}
                  minLength={2}
                  maxLength={20}
                />
                {validationErrors.user_id && (
                  <div style={errorMessageStyle}>
                    {validationErrors.user_id}
                  </div>
                )}
              </div>
            )}

            {basicFields.password && (
              <div className="signup-form-group">
                <label htmlFor="password" style={labelStyle}>
                  <span style={{ color: requiredMarkColor }}>* </span>
                  {t('비밀번호')}
                </label>
                <input 
                  id="password" 
                  type="password" 
                  placeholder={t('비밀번호 입력')} 
                  required 
                  style={validationErrors.password ? inputErrorStyle : inputStyle}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                />
                {validationErrors.password && (
                  <div style={errorMessageStyle}>
                    {validationErrors.password}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="signup-form-row">
            {basicFields.name && (
              <div className="signup-form-group">
                <label htmlFor="name" style={labelStyle}>
                  <span style={{ color: requiredMarkColor }}>* </span>
                  {t('이름')}
                </label>
                <input 
                  id="name" 
                  type="text" 
                  placeholder={t('이름 입력')} 
                  required 
                  style={validationErrors.name ? inputErrorStyle : inputStyle}
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                />
                {validationErrors.name && (
                  <div style={errorMessageStyle}>
                    {validationErrors.name}
                  </div>
                )}
              </div>
            )}
            
            {basicFields.phone && (
              <div className="signup-form-group">
                <label htmlFor="phone" style={labelStyle}>
                  <span style={{ color: requiredMarkColor }}>* </span>
                  {t('핸드폰 번호')}
                </label>
                <input 
                  id="phone" 
                  type="tel" 
                  placeholder={t('핸드폰 번호 입력')} 
                  required 
                  style={validationErrors.phone ? inputErrorStyle : inputStyle}
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={() => handleBlur('phone')}
                />
                {validationErrors.phone && (
                  <div style={errorMessageStyle}>
                    {validationErrors.phone}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {(basicFields.email || basicFields.birthday) && (
            <div className="signup-form-row">
              {basicFields.email && (
                <div className="signup-form-group">
                  <label htmlFor="email" style={labelStyle}>
                    {t('이메일')}
                  </label>
                  <input 
                    id="email" 
                    type="email" 
                    placeholder={t('이메일 입력')} 
                    style={validationErrors.email ? inputErrorStyle : inputStyle}
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => handleBlur('email')}
                  />
                  {validationErrors.email && (
                    <div style={errorMessageStyle}>
                      {validationErrors.email}
                    </div>
                  )}
                </div>
              )}
              
              {basicFields.birthday && (
                <div className="signup-form-group">
                  <label htmlFor="birthday" style={labelStyle}>
                    {t('생년월일')}
                  </label>
                  <input 
                    id="birthday" 
                    type="date" 
                    style={inputStyle}
                    value={formData.birthday}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          )}
          
          {basicFields.address && (
            <div className="signup-form-group">
              <label htmlFor="address" style={labelStyle}>
                {t('주소')}
              </label>
              <input 
                id="address" 
                type="text" 
                placeholder={t('주소 입력')} 
                style={inputStyle}
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          )}
          
          {basicFields.referralCode && (
            <div className="signup-form-group">
              <label htmlFor="referral_code" style={labelStyle}>
                {t('추천인 코드')}
              </label>
              <input 
                id="referral_code" 
                type="text" 
                placeholder={t('추천인 코드 입력')} 
                style={inputStyle}
                value={formData.referral_code}
                onChange={handleChange}
                disabled={formData.referral_code_from_url}
              />
            </div>
          )}

          {/* SPECIFIED policy fields */}
          {isSpecifiedLegPolicy && (
            <div className="signup-form-row">
              <div className="signup-form-group">
                <label htmlFor="upperer_code" style={labelStyle}>
                  {t('후원인 코드')}
                </label>
                <input 
                  id="upperer_code" 
                  type="text" 
                  placeholder={t('후원인 코드 입력')} 
                  style={inputStyle}
                  value={formData.upperer_code}
                  onChange={handleChange}
                />
              </div>
              
              <div className="signup-form-group">
                <label htmlFor="left_or_right" style={labelStyle}>
                  {t('좌/우 선택')}
                </label>
                <select
                  id="left_or_right"
                  style={inputStyle}
                  value={formData.left_or_right}
                  onChange={handleChange}
                >
                  <option value="0">{t('좌')}</option>
                  <option value="1">{t('우')}</option>
                </select>
              </div>
            </div>
          )}

          {/* Company specific fields */}
          {(companyId === 24 || companyId === 26) && (
            <div className="signup-form-row">
              <div className="signup-form-group">
                <label htmlFor="center_code" style={labelStyle}>
                  <span style={{ color: requiredMarkColor }}>* </span>
                  {t('Leader Code')}
                </label>
                <input 
                  id="center_code" 
                  type="text" 
                  placeholder={t('Leader Code')} 
                  style={inputStyle}
                  value={formData.center_code}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          )}

          {(companyId === 24 || companyId === 26) && (
            <div className="signup-form-row">
              {companyId === 24 && (
                <div className="signup-form-group">
                  <label htmlFor="trxWithdrawAddress" style={labelStyle}>
                    {t('TRX 지갑주소')}
                  </label>
                  <input 
                    id="trxWithdrawAddress" 
                    type="text" 
                    placeholder={t('TRX 지갑주소 입력')} 
                    style={inputStyle}
                    value={formData.trxWithdrawAddress}
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="signup-form-group">
                <label htmlFor="bscWithdrawAddress" style={labelStyle}>
                  {t('USDT-BNB 지갑주소')}
                </label>
                <input 
                  id="bscWithdrawAddress" 
                  type="text" 
                  placeholder={t('USDT-BNB 지갑주소 입력')} 
                  style={inputStyle}
                  value={formData.bscWithdrawAddress}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* Custom fields section */}
          {varFields && (varFields.var01?.show || varFields.var02?.show || varFields.var03?.show || 
                         varFields.var04?.show || varFields.var05?.show || varFields.var06?.show || 
                         varFields.var07?.show || varFields.var08?.show || varFields.var09?.show || 
                         varFields.var10?.show) && (
            <div className="signup-custom-fields-section">
              <div style={sectionTitleStyle}>{t('추가 정보')}</div>
              
              {varFields.var01?.show && (
                <div className="signup-form-row">
                  {renderVarField('var01', varFields.var01, { width: '100%' })}
                </div>
              )}
              
              {(varFields.var02?.show || varFields.var03?.show) && (
                <div className="signup-form-row">
                  {varFields.var02?.show && renderVarField('var02', varFields.var02)}
                  {varFields.var03?.show && renderVarField('var03', varFields.var03)}
                </div>
              )}
              
              {(varFields.var04?.show || varFields.var05?.show) && (
                <div className="signup-form-row">
                  {varFields.var04?.show && renderVarField('var04', varFields.var04)}
                  {varFields.var05?.show && renderVarField('var05', varFields.var05)}
                </div>
              )}
              
              {(varFields.var06?.show || varFields.var07?.show) && (
                <div className="signup-form-row">
                  {varFields.var06?.show && renderVarField('var06', varFields.var06)}
                  {varFields.var07?.show && renderVarField('var07', varFields.var07)}
                </div>
              )}
              
              {(varFields.var08?.show || varFields.var09?.show) && (
                <div className="signup-form-row">
                  {varFields.var08?.show && renderVarField('var08', varFields.var08)}
                  {varFields.var09?.show && renderVarField('var09', varFields.var09)}
                </div>
              )}
              
              {varFields.var10?.show && (
                <div className="signup-form-row">
                  {renderVarField('var10', varFields.var10, { width: '100%' })}
                </div>
              )}
            </div>
          )}

          <button 
            type="submit" 
            style={buttonStyle}
            disabled={loading}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = buttonHoverColor;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonColor;
            }}
          >
            {loading ? t('처리 중...') : t('회원가입')}
          </button>
        </form>
      )}
    </div>
  );
};

export default BasicSignupSkin;